import React, { useState, useMemo } from 'react';
import { findSkinImage } from './skinIndex';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';

const CONDITION_MAP = {
  'Factory New': 'Factory New', 'Minimal Wear': 'Minimal Wear',
  'Field Tested': 'Field-Tested', 'Well Worn': 'Well-Worn', 'Battle Scarred': 'Battle-Scarred',
};


function getLiveMarketPrice(livePrices, row) {
  if (!livePrices || !row.skin?.value) return null;
  const cond = row.condition?.value;
  if (cond) {
    const mapped = CONDITION_MAP[cond] || cond;
    return livePrices[`${row.skin.value} (${mapped})`]?.starting_at?.price
      ?? livePrices[row.skin.value]?.starting_at?.price
      ?? null;
  }
  return livePrices[row.skin.value]?.starting_at?.price ?? null;
}

function MiniSparkline({ data, width = 80, height = 26 }) {
  const prices = (data || []).map(d => d.buffPrice ?? d.price).filter(p => p != null);
  if (prices.length < 2) return null;
  const min   = Math.min(...prices);
  const max   = Math.max(...prices);
  const range = max - min || 1;
  const pts   = prices.map((p, i) => {
    const x = (i / (prices.length - 1)) * width;
    const y = height - ((p - min) / range) * (height - 3) - 1.5;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const trend = prices[prices.length - 1] >= prices[0];
  return (
    <svg width={width} height={height} style={{ display: 'block', overflow: 'visible', flexShrink: 0 }}>
      <polyline points={pts} fill="none"
        stroke={trend ? '#4ade80' : '#f87171'} strokeWidth="1.5"
        strokeLinejoin="round" strokeLinecap="round" />
      <circle
        cx={pts.split(' ').at(-1).split(',')[0]}
        cy={pts.split(' ').at(-1).split(',')[1]}
        r="2.5" fill={trend ? '#4ade80' : '#f87171'} />
    </svg>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#0d1425', border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 10, padding: '10px 14px', fontSize: '0.96rem',
    }}>
      <div style={{ color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontWeight: 700, color: '#4ade80', fontSize: '1.05rem' }}>
        ${payload[0].value?.toFixed(2)}
      </div>
    </div>
  );
}

// ── Edit modal ────────────────────────────────────────────
function EditInvestmentModal({ row, img, marketPrice, onSave, onClose }) {
  const [buyPrice, setBuyPrice]     = useState(parseFloat(row.buyPrice) > 0 ? String(row.buyPrice) : '');
  const [description, setDescription] = useState(row.description || '');

  const inp = {
    padding: '8px 12px', width: '100%', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 10, color: 'var(--text)', fontSize: '0.98rem',
    fontFamily: 'var(--font-body)', outline: 'none',
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#0b1220', border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 20, width: 'min(420px, 100%)', padding: '26px',
        boxShadow: '0 24px 70px rgba(0,0,0,0.65)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
          {img && (
            <div style={{ width: 72, height: 48, flexShrink: 0, background: 'rgba(0,0,0,0.25)', borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={img} alt="" style={{ width: 64, height: 42, objectFit: 'contain' }} />
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', fontWeight: 700, color: '#fff',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.skin?.value}
            </div>
            {row.condition?.value && (
              <div style={{ fontSize: '0.92rem', color: 'var(--text-dim)', marginTop: 2 }}>{row.condition.value}</div>
            )}
            {marketPrice != null && (
              <div style={{ fontSize: '0.92rem', color: 'var(--text-dim)', marginTop: 2 }}>
                Market: <strong style={{ color: 'var(--text)' }}>${marketPrice.toFixed(2)}</strong>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Buy price */}
          <div>
            <label style={{ fontSize: '0.92rem', color: 'var(--text-dim)', display: 'block', marginBottom: 5 }}>
              Buy Price ($)
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--text-dim)', fontSize: '0.98rem', pointerEvents: 'none' }}>$</span>
              <input
                type="number" min="0" step="0.01" autoFocus
                value={buyPrice}
                onChange={e => setBuyPrice(e.target.value)}
                placeholder="0.00"
                style={{ ...inp, paddingLeft: 26 }}
              />
            </div>
            {marketPrice != null && buyPrice !== '' && parseFloat(buyPrice) > 0 && (
              <div style={{ marginTop: 5, fontSize: '0.92rem' }}>
                {(() => {
                  const bp = parseFloat(buyPrice);
                  const pnl = marketPrice - bp;
                  const pct = (pnl / bp * 100).toFixed(1);
                  const c   = pnl >= 0 ? '#4ade80' : '#f87171';
                  return (
                    <span style={{ color: c }}>
                      {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)} ({pnl >= 0 ? '+' : ''}{pct}%) vs market
                    </span>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Note */}
          <div>
            <label style={{ fontSize: '0.92rem', color: 'var(--text-dim)', display: 'block', marginBottom: 5 }}>
              Note
            </label>
            <input value={description} onChange={e => setDescription(e.target.value)}
              placeholder="e.g. sticker craft, factory new float…" style={inp} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 22 }}>
          <button onClick={onClose} style={{
            padding: '8px 18px', borderRadius: 10, border: '1px solid var(--border)',
            background: 'transparent', color: 'var(--text-muted)', fontSize: '0.98rem', cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={() => { onSave({ buyPrice: buyPrice || '', description: description.trim() }); onClose(); }} style={{
            padding: '8px 22px', borderRadius: 10, border: 'none',
            background: 'var(--green)', color: '#000', fontSize: '0.99rem', fontWeight: 700, cursor: 'pointer',
          }}>Save</button>
        </div>
      </div>
    </div>
  );
}

function InvestmentCard({ row, img, marketPrice, buyPrice, dailyChange, dailyPct, pnl, pnlPct, history, profC, lossC, onMarkSoldTraded, onDelete, onEdit }) {
  const [hovered, setHovered] = useState(false);
  const isGaining = dailyChange != null && dailyChange > 0;
  const isLosing  = dailyChange != null && dailyChange < 0;
  const barColor  = isGaining ? '#4ade80' : isLosing ? '#f87171' : 'rgba(255,255,255,0.07)';

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onEdit}
      style={{
        display: 'flex', alignItems: 'stretch', borderRadius: 14, overflow: 'hidden',
        background: 'var(--panel)', cursor: 'pointer',
        border: `1px solid ${isGaining ? 'rgba(74,222,128,0.22)' : isLosing ? 'rgba(248,113,113,0.18)' : 'var(--border)'}`,
        transition: 'border-color 0.15s, background 0.12s',
        ...(hovered && { background: 'rgba(255,255,255,0.025)' }),
      }}
    >
      {/* Trend color bar */}
      <div style={{ width: 3, flexShrink: 0, background: barColor }} />

      {/* Image */}
      <div style={{
        width: 90, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.20)', padding: '8px 4px',
      }}>
        {img
          ? <img src={img} alt="" style={{ width: 78, height: 52, objectFit: 'contain' }} loading="lazy" />
          : <div style={{ width: 78, height: 52, borderRadius: 6, background: 'rgba(255,255,255,0.04)' }} />
        }
      </div>

      {/* Name + sparkline */}
      <div style={{ flex: 1, padding: '10px 14px', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
        <div style={{ fontSize: '1.01rem', fontWeight: 700, color: '#fff',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.skin?.value}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {row.condition?.value && (
            <span style={{ fontSize: '0.9rem', color: 'var(--text-dim)' }}>{row.condition.value}</span>
          )}
          {row.description && (
            <span style={{ fontSize: '0.89rem', color: 'rgba(255,255,255,0.22)', fontStyle: 'italic' }}>
              {row.description}
            </span>
          )}
        </div>
        {history.length >= 2 && (
          <div style={{ marginTop: 4 }}>
            <MiniSparkline data={history} width={80} height={24} />
          </div>
        )}
      </div>

      {/* Buy */}
      <div style={{ padding: '10px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', flexShrink: 0 }}>
        <div style={{ fontSize: '0.87rem', color: 'var(--text-dim)', marginBottom: 2 }}>Buy</div>
        <div style={{ fontSize: '0.99rem', fontWeight: 600, color: 'var(--text-muted)' }}>
          {buyPrice > 0 ? `$${buyPrice.toFixed(2)}` : '—'}
        </div>
      </div>

      {/* Market */}
      <div style={{ padding: '10px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', flexShrink: 0 }}>
        <div style={{ fontSize: '0.87rem', color: 'var(--text-dim)', marginBottom: 2 }}>Market</div>
        <div style={{ fontSize: '1.01rem', fontWeight: 700, color: '#fff' }}>
          {marketPrice != null ? `$${marketPrice.toFixed(2)}` : '—'}
        </div>
      </div>

      {/* Today's change */}
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', flexShrink: 0, minWidth: 82 }}>
        <div style={{ fontSize: '0.87rem', color: 'var(--text-dim)', marginBottom: 2 }}>Today</div>
        {dailyChange != null ? (
          <>
            <div style={{ fontSize: '1.01rem', fontWeight: 700, color: isGaining ? profC : isLosing ? lossC : 'var(--text)' }}>
              {isGaining ? '+' : isLosing ? '-' : ''}${Math.abs(dailyChange).toFixed(2)}
            </div>
            {dailyPct != null && (
              <div style={{ fontSize: '0.89rem', color: isGaining ? profC : isLosing ? lossC : 'var(--text-dim)', opacity: 0.85 }}>
                {isGaining ? '+' : ''}{dailyPct.toFixed(2)}%
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.2)' }}>—</div>
        )}
      </div>

      {/* P&L since buy */}
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', flexShrink: 0, minWidth: 84 }}>
        <div style={{ fontSize: '0.87rem', color: 'var(--text-dim)', marginBottom: 2 }}>P&L</div>
        {pnl != null ? (
          <>
            <div style={{ fontSize: '1.01rem', fontWeight: 700, color: pnl >= 0 ? profC : lossC }}>
              {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
            </div>
            {pnlPct != null && (
              <div style={{ fontSize: '0.89rem', color: pnl >= 0 ? profC : lossC, opacity: 0.85 }}>
                {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.2)' }}>—</div>
        )}
      </div>

      {/* Actions */}
      <div onClick={e => e.stopPropagation()} style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 5, flexShrink: 0 }}>
        <button
          onClick={onMarkSoldTraded}
          title="Sell or trade this item"
          style={{
            padding: '4px 11px', borderRadius: 7, cursor: 'pointer',
            fontSize: '0.91rem', fontWeight: 700, whiteSpace: 'nowrap',
            border: '1px solid rgba(245,158,11,0.35)',
            background: 'rgba(245,158,11,0.08)', color: '#f59e0b',
          }}
        >
          Sell / Trade
        </button>
        <button
          onClick={onDelete}
          title="Remove from investments (returns to inventory)"
          style={{
            padding: '4px 11px', borderRadius: 7, cursor: 'pointer',
            fontSize: '0.91rem', fontWeight: 600, whiteSpace: 'nowrap',
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.35)',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.borderColor = 'rgba(248,113,113,0.30)'; e.currentTarget.style.background = 'rgba(248,113,113,0.07)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

export default function InvestmentsTab({ rows, settings, skinsData, livePrices, onDeleteRow, onMarkSoldTraded, onUpdateRow }) {
  const [filter, setFilter]       = useState('all');
  const [sortBy, setSortBy]       = useState('change');
  const [sortDir, setSortDir]     = useState('desc');
  const [editingItem, setEditingItem] = useState(null); // { data } object from filtered list

  const profC = settings?.profitTextColor || '#4ade80';
  const lossC = settings?.lossTextColor   || '#f87171';

  // Only live (not sold, not traded) investment rows
  const investmentRows = useMemo(() =>
    (rows || []).filter(r =>
      r.isInvestment &&
      r.skin?.value &&
      !(parseFloat(r.sellPrice) > 0) &&
      !r.tradeId
    )
  , [rows]);

  // Enrich each item with price data
  const items = useMemo(() => {
    return investmentRows.map(row => {
      const history   = row.priceHistory || [];
      const last      = history[history.length - 1];
      const prev      = history[history.length - 2];
      const lastPrice = last ? (last.buffPrice ?? last.price ?? null) : null;
      const prevPrice = prev ? (prev.buffPrice ?? prev.price ?? null) : null;
      const livePrice = getLiveMarketPrice(livePrices, row);
      const marketPrice  = lastPrice ?? livePrice;
      const dailyChange  = lastPrice != null && prevPrice != null ? lastPrice - prevPrice : null;
      const dailyPct     = dailyChange != null && prevPrice ? (dailyChange / prevPrice * 100) : null;
      const buyPrice     = parseFloat(row.buyPrice) || 0;
      const pnl          = marketPrice != null && buyPrice > 0 ? marketPrice - buyPrice : null;
      const pnlPct       = pnl != null && buyPrice > 0 ? (pnl / buyPrice * 100) : null;
      const img          = findSkinImage(skinsData, row.skin?.value, row.condition?.value);
      return { row, marketPrice, prevPrice, dailyChange, dailyPct, pnl, pnlPct, buyPrice, img, history };
    });
  }, [investmentRows, livePrices, skinsData]);

  // Build portfolio history using time-weighted deltas so that adding new items
  // to investments doesn't cause a spike — only price movements of already-held
  // items shift the line.
  const portfolioHistory = useMemo(() => {
    // Per-item lookup: date → price
    const priceMaps = items.map(({ row }) => {
      const m = {};
      for (const snap of (row.priceHistory || [])) {
        const p = snap.buffPrice ?? snap.price ?? null;
        if (p != null) m[snap.date] = p;
      }
      return m;
    });

    // All dates that appear in any item's history, sorted ascending
    const allDates = [...new Set(
      items.flatMap(({ row }) => (row.priceHistory || []).map(s => s.date))
    )].sort();

    if (allDates.length === 0) return [];

    let runningValue = null;
    const result = [];

    for (let di = 0; di < allDates.length; di++) {
      const date     = allDates[di];
      const prevDate = di > 0 ? allDates[di - 1] : null;

      if (prevDate === null) {
        // Baseline: actual sum of all items with a price on the first date
        runningValue = priceMaps.reduce((s, m) => s + (m[date] ?? 0), 0);
      } else {
        // Only accumulate deltas for items that had a price on BOTH this date and
        // the previous date — newly added items don't inflate the line.
        let delta = 0;
        for (const m of priceMaps) {
          const curr = m[date];
          const prev = m[prevDate];
          if (curr != null && prev != null) delta += curr - prev;
        }
        runningValue = runningValue + delta;
      }

      result.push({
        date,
        label: new Date(date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: parseFloat(runningValue.toFixed(2)),
      });
    }

    return result;
  }, [items]);

  // Summary stats
  const totalValue       = items.reduce((s, i) => s + (i.marketPrice || 0), 0);
  const totalCost        = items.reduce((s, i) => s + i.buyPrice, 0);
  const totalPnl         = totalCost > 0 ? totalValue - totalCost : null;
  const totalPnlPct      = totalPnl != null && totalCost > 0 ? (totalPnl / totalCost * 100) : null;
  const totalDailyChange = items.reduce((s, i) => s + (i.dailyChange || 0), 0);
  const prevPortfolio    = portfolioHistory.length >= 2 ? portfolioHistory[portfolioHistory.length - 2]?.value : null;
  const dailyPct         = prevPortfolio ? (totalDailyChange / prevPortfolio * 100) : null;
  const gainingCount     = items.filter(i => i.dailyChange != null && i.dailyChange > 0).length;
  const losingCount      = items.filter(i => i.dailyChange != null && i.dailyChange < 0).length;

  // Chart color based on overall trend
  const chartFirst = portfolioHistory[0]?.value;
  const chartLast  = portfolioHistory[portfolioHistory.length - 1]?.value;
  const chartUp    = portfolioHistory.length < 2 || (chartLast != null && chartFirst != null && chartLast >= chartFirst);
  const chartColor = chartUp ? '#4ade80' : '#f87171';

  // Filter + sort
  const filtered = useMemo(() => {
    let out = [...items];
    if (filter === 'gaining') out = out.filter(i => i.dailyChange != null && i.dailyChange > 0);
    if (filter === 'losing')  out = out.filter(i => i.dailyChange != null && i.dailyChange < 0);
    out.sort((a, b) => {
      const INF = sortDir === 'desc' ? -Infinity : Infinity;
      let av, bv;
      if      (sortBy === 'change') { av = a.dailyPct  ?? INF; bv = b.dailyPct  ?? INF; }
      else if (sortBy === 'value')  { av = a.marketPrice ?? 0; bv = b.marketPrice ?? 0; }
      else if (sortBy === 'pnl')    { av = a.pnl ?? INF;        bv = b.pnl ?? INF; }
      else if (sortBy === 'buy')    { av = a.buyPrice;           bv = b.buyPrice; }
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return out;
  }, [items, filter, sortBy, sortDir]);

  if (investmentRows.length === 0) {
    return (
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '2.2rem', marginBottom: 12 }}>📈</div>
        <div style={{ fontSize: '1.09rem', fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
          No investments tracked
        </div>
        <div style={{ fontSize: '0.97rem', color: 'var(--text-dim)', maxWidth: 360, margin: '0 auto', lineHeight: 1.6 }}>
          Go to the <strong style={{ color: 'var(--text)' }}>Live</strong> tab and click the
          {' '}<strong style={{ color: '#4ade80' }}>📈</strong> button on any item card to start tracking it here.
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>

      {/* ── Summary header ─────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: '0.91rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: 6 }}>
            Investment Portfolio · {items.length} item{items.length !== 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: '#fff', lineHeight: 1, letterSpacing: '-0.02em' }}>
            ${totalValue.toFixed(2)}
          </div>
          <div style={{ display: 'flex', gap: 18, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {totalPnl != null && (
              <span style={{ fontSize: '0.99rem', fontWeight: 600, color: totalPnl >= 0 ? profC : lossC }}>
                {totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(2)} overall
                {totalPnlPct != null && (
                  <span style={{ opacity: 0.7, fontSize: '0.95rem' }}>
                    {' '}({totalPnlPct >= 0 ? '+' : ''}{totalPnlPct.toFixed(1)}%)
                  </span>
                )}
              </span>
            )}
            {items.some(i => i.dailyChange != null) && (
              <span style={{ fontSize: '0.99rem', fontWeight: 600, color: totalDailyChange >= 0 ? profC : lossC }}>
                {totalDailyChange >= 0 ? '+' : ''}${totalDailyChange.toFixed(2)} today
                {dailyPct != null && (
                  <span style={{ opacity: 0.7, fontSize: '0.95rem' }}>
                    {' '}({dailyPct >= 0 ? '+' : ''}{dailyPct.toFixed(1)}%)
                  </span>
                )}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Cost',   value: `$${totalCost.toFixed(2)}`,  color: 'var(--text-muted)' },
            { label: 'Gaining',      value: gainingCount,                 color: profC },
            { label: 'Losing',       value: losingCount,                  color: lossC },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.87rem', color: 'var(--text-dim)', marginBottom: 3 }}>{s.label}</div>
              <div style={{ fontSize: '1.09rem', fontWeight: 700, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Portfolio chart ─────────────────────────────── */}
      {portfolioHistory.length >= 2 && (
        <div style={{
          background: 'var(--panel)', border: '1px solid var(--border)',
          borderRadius: 18, padding: '20px 16px 10px', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: 8, marginBottom: 14 }}>
            <div style={{ fontSize: '0.92rem', color: 'var(--text-dim)' }}>
              Portfolio Value — {portfolioHistory.length} day{portfolioHistory.length !== 1 ? 's' : ''} tracked
            </div>
            {chartFirst != null && chartLast != null && (
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: chartUp ? profC : lossC }}>
                {chartUp ? '▲' : '▼'} {chartUp ? '+' : ''}${(chartLast - chartFirst).toFixed(2)} since start
              </div>
            )}
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={portfolioHistory} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="investPortfolioGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={chartColor} stopOpacity={0.28} />
                  <stop offset="95%" stopColor={chartColor} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="label"
                tick={{ fontSize: 14.5, fill: 'rgba(255,255,255,0.35)' }}
                axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis
                tick={{ fontSize: 14.5, fill: 'rgba(255,255,255,0.35)' }}
                axisLine={false} tickLine={false} width={62}
                tickFormatter={v => `$${v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v.toFixed(0)}`} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="value"
                stroke={chartColor} strokeWidth={2}
                fill="url(#investPortfolioGrad)"
                dot={false} activeDot={{ r: 4, fill: chartColor, strokeWidth: 0 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Filter + sort bar ───────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {[
          { key: 'all',     label: 'All',       activeColor: '#fff',    activeBg: 'rgba(255,255,255,0.12)', activeBorder: 'rgba(255,255,255,0.22)' },
          { key: 'gaining', label: '↑ Gaining', activeColor: profC,     activeBg: 'rgba(74,222,128,0.15)',  activeBorder: 'rgba(74,222,128,0.32)' },
          { key: 'losing',  label: '↓ Losing',  activeColor: lossC,     activeBg: 'rgba(248,113,113,0.15)', activeBorder: 'rgba(248,113,113,0.32)' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '5px 16px', borderRadius: 20, cursor: 'pointer', fontSize: '0.95rem',
            fontWeight: filter === f.key ? 700 : 400,
            background: filter === f.key ? f.activeBg : 'rgba(255,255,255,0.04)',
            color: filter === f.key ? f.activeColor : 'var(--text-muted)',
            border: filter === f.key ? `1px solid ${f.activeBorder}` : '1px solid rgba(255,255,255,0.06)',
            transition: 'all 0.12s',
          }}>{f.label}</button>
        ))}

        <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.08)', margin: '0 4px' }} />

        <span style={{ fontSize: '0.92rem', color: 'var(--text-dim)' }}>Sort:</span>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{
          padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(255,255,255,0.07)', color: '#fff',
          fontSize: '0.95rem', fontFamily: 'var(--font-body)', outline: 'none', cursor: 'pointer',
        }}>
          <option value="change">% Daily Change</option>
          <option value="value">Market Value</option>
          <option value="pnl">P&L</option>
          <option value="buy">Buy Price</option>
        </select>

        <button onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')} style={{
          padding: '4px 11px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)',
          fontSize: '0.94rem', cursor: 'pointer', fontFamily: 'var(--font-body)',
        }}>
          {sortDir === 'desc' ? '↓ High → Low' : '↑ Low → High'}
        </button>

        <span style={{ marginLeft: 'auto', fontSize: '0.92rem', color: 'var(--text-dim)' }}>
          {filtered.length} of {items.length}
        </span>
      </div>

      {/* ── Item list ───────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.map(data => (
          <InvestmentCard
            key={data.row.timestamp || data.row.skin?.value}
            {...data}
            profC={profC}
            lossC={lossC}
            onEdit={() => setEditingItem(data)}
            onMarkSoldTraded={() => onMarkSoldTraded?.(data.row)}
            onDelete={() => onDeleteRow?.(data.row)}
          />
        ))}
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-dim)', fontSize: '0.97rem' }}>
            No items match this filter.
          </div>
        )}
      </div>

      {/* ── Edit modal ──────────────────────────────────── */}
      {editingItem && (
        <EditInvestmentModal
          row={editingItem.row}
          img={editingItem.img}
          marketPrice={editingItem.marketPrice}
          onSave={updates => onUpdateRow?.(editingItem.row, updates)}
          onClose={() => setEditingItem(null)}
        />
      )}
    </div>
  );
}
